// src/pages/IndustryApplicationsAmazon/IndustryApplications.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Container, Grid, Card } from '@mui/material';
import { useTranslation } from 'react-i18next';

import UniversalNav from '../../components/UniversalNav/UniversalNav';
import { useIndustryData } from './IndustryData'; // IMPORT AS HOOK
import InteractiveHeatmap from './InteractiveHeatmap';
import InteractiveShield from './InteractiveShield';
import InteractiveAudioPlayer from './InteractiveAudioPlayer';
import InteractiveQRCode from './InteractiveQRCode';
import InteractiveTextHighlight from './InteractiveTextHighlight';
import AmazonStorePipelineAnimation from './animations/AmazonStorePipelineAnimation';
import ReviewsPipelineAnimation from './animations/ReviewsPipelineAnimation';
import KdpPipelineAnimation from './animations/KdpPipelineAnimation';
import AmcPipelineAnimation from './animations/AmcPipelineAnimation';
import AmcDashboardVisual from './animations/AmcDashboardVisual';
import AlexaPipelineAnimation from './animations/AlexaPipelineAnimation';
import SponsoredBrandsPipelineAnimation from './animations/SponsoredBrandsPipelineAnimation';
import AwsPipelineAnimation from './animations/AwsPipelineAnimation';
import AwsChainOfCustodyVisual from './animations/AwsChainOfCustodyVisual';

import './IndustryApplications.css';

// -------------------------------------------------------------
// SAFE IMAGE LOADERS
// -------------------------------------------------------------
const getIndustryImage = (id) => {
    try { return require(`./assets/industry/${id}.webp`); } 
    catch (e) { return null; }
};

const getApplicationImage = (id, index) => {
    try { return require(`./assets/application/${id}_${index}.webp`); } 
    catch (e) { return null; }
};

// -------------------------------------------------------------
// SVG TRIGONOMETRY HELPERS
// -------------------------------------------------------------
const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  };
};

const describeArc = (x, y, innerRadius, outerRadius, startAngle, endAngle) => {
  const actualEndAngle = endAngle - 1.5; 
  const startOuter = polarToCartesian(x, y, outerRadius, actualEndAngle);
  const endOuter = polarToCartesian(x, y, outerRadius, startAngle);
  const startInner = polarToCartesian(x, y, innerRadius, actualEndAngle);
  const endInner = polarToCartesian(x, y, innerRadius, startAngle);
  const largeArcFlag = actualEndAngle - startAngle <= 180 ? "0" : "1";

  return [
    "M", startOuter.x, startOuter.y,
    "A", outerRadius, outerRadius, 0, largeArcFlag, 0, endOuter.x, endOuter.y,
    "L", endInner.x, endInner.y,
    "A", innerRadius, innerRadius, 0, largeArcFlag, 1, startInner.x, startInner.y,
    "Z"
  ].join(" ");
};

const IndustryApplications = () => {
    const { t } = useTranslation();
    const INDUSTRIES_DATA = useIndustryData(); // INITIALIZE DATA HOOK
    
    const [activeIndex, setActiveIndex] = useState(0);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [activeSolutionIndex, setActiveSolutionIndex] = useState(0);
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    // Highly efficient scroll handler
    const handleScroll = (e) => {
        setIsScrolled(e.currentTarget.scrollTop > 20);
    };

    useEffect(() => {
        setActiveSolutionIndex(0);
    }, [activeIndex]);

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    const particlesConfig = { 
        background: { color: { value: 'transparent' } },
        fpsLimit: 120,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true }, modes: { repulse: { distance: 100, duration: 0.4 } } },
        particles: { color: { value: '#ffffff' }, links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.4, width: 1 }, move: { enable: true, speed: 1 }, number: { value: 80 }, opacity: { value: 0.5 }, shape: { type: 'circle' }, size: { value: { min: 1, max: 3 } } },
        detectRetina: true,
    };

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTo(0, 0);
        }
    }, []);

    const activeIndustry = INDUSTRIES_DATA[activeIndex];
    const displayIndustry = hoveredIndex !== null ? INDUSTRIES_DATA[hoveredIndex] : activeIndustry;
    const activeSolution = activeIndustry.solutionPoints[activeSolutionIndex];
    const currentMediaSrc = activeSolution.mediaUrl || getApplicationImage(activeIndustry.id, activeSolutionIndex);

    const svgSize = 500;
    const center = svgSize / 2;
    const outerRadius = 245;
    const innerRadius = 100; 
    const sliceAngle = 360 / INDUSTRIES_DATA.length;

    const renderInteractiveMedia = () => {
        if (activeSolution.renderType === 'amazon-store-pipeline') return <AmazonStorePipelineAnimation />;
        if (activeSolution.renderType === 'reviews-pipeline') return <ReviewsPipelineAnimation />;
        if (activeSolution.renderType === 'kdp-pipeline') return <KdpPipelineAnimation />;
        if (activeSolution.renderType === 'amc-pipeline') return <AmcPipelineAnimation />;
        if (activeSolution.renderType === 'amc-dashboard') return <AmcDashboardVisual infoPoint={activeSolution.infoPoint} />;
        if (activeSolution.renderType === 'alexa-pipeline') return <AlexaPipelineAnimation />;
        if (activeSolution.renderType === 'sponsored-brands-pipeline') return <SponsoredBrandsPipelineAnimation />;
        if (activeSolution.renderType === 'aws-pipeline') return <AwsPipelineAnimation />;
        if (activeSolution.renderType === 'aws-chain-of-custody') return <AwsChainOfCustodyVisual />;
        
        if (activeSolution.renderType === 'text-highlight') {
            return (
                <InteractiveTextHighlight 
                    text={activeSolution.textContent} 
                    regions={activeSolution.regions}
                    infoPoint={activeSolution.infoPoint}
                    verifyUrl={activeSolution.verifyUrl}
                    shieldIcon={activeSolution.shieldIcon}
                />
            );
        }

        if (activeSolution.renderType === 'audio-player') {
            return (
                <InteractiveAudioPlayer 
                    mediaUrl={activeSolution.mediaUrl}
                    imageSrc={activeSolution.mediaImage}
                    infoPoint={activeSolution.infoPoint}
                    verifyUrl={activeSolution.verifyUrl}
                    shieldIcon={activeSolution.shieldIcon}
                />
            );
        }
        
        if (!currentMediaSrc) {
            return (
                <div className="placeholder-image">
                    <Typography variant="button" sx={{ opacity: 0.7, textAlign: 'center' }}>
                        {t('industryApplicationsAmazon.page.missingMedia', { id: `${activeIndustry.id}_${activeSolutionIndex}` })}
                    </Typography>
                </div>
            );
        }

        switch (activeSolution.renderType) {
            case 'heatmap':
                return (
                    <InteractiveHeatmap 
                        imageSrc={currentMediaSrc} 
                        regions={activeSolution.regions} 
                        infoPoint={activeSolution.infoPoint} 
                    />
                );
            case 'shield':
                return (
                    <InteractiveShield 
                        imageSrc={currentMediaSrc} 
                        mediaType={activeSolution.mediaType}
                        verifyUrl={activeSolution.verifyUrl}
                        shieldIcon={activeSolution.shieldIcon}
                        shieldPosition={activeSolution.shieldPosition}
                        tooltipColor={activeSolution.tooltipColor}
                        floatingIcons={activeSolution.floatingIcons}
                    />
                );
            case 'qr-code':
                return (
                    <InteractiveQRCode 
                        imageSrc={currentMediaSrc} 
                        verifyUrl={activeSolution.verifyUrl}
                        qrIcon={activeSolution.qrIcon}
                        qrPosition={activeSolution.qrPosition}
                    />
                );
            default:
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                        <img 
                            src={currentMediaSrc} 
                            alt={`${activeIndustry.title} Application`} 
                            style={{ maxWidth: '100%', maxHeight: '350px', objectFit: 'contain', display: 'block' }}
                        />
                    </div>
                );
        }
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className="industry-page-container"
        >
            <Particles id="tsparticles-industry" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('industryApplicationsAmazon.helmet.title')}</title>
                <meta name="description" content={t('industryApplicationsAmazon.helmet.description')} />
            </Helmet>
            
            <div className="industry-nav-container" data-scrolled={isScrolled}>
                <UniversalNav />
            </div>

            <main className="industry-content-wrapper">
                <Box className="industry-page-hero">
                    <Container maxWidth="lg" sx={{ textAlign: 'center', pt: 0, pb: 2 }}> 
                        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                            <Typography component="h1" variant="h3" className="industry-page-title">
                                {t('industryApplicationsAmazon.page.title')}
                            </Typography>
                            <Typography variant="body1" className="industry-page-subtitle" sx={{ fontSize: '0.95rem' }}>
                                {t('industryApplicationsAmazon.page.subtitle')}
                            </Typography>
                        </motion.div>
                    </Container>
                </Box>

                <Container maxWidth="xl" sx={{ pb: 4 }}>
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={5} lg={4}>
                            <Box className="donut-container">
                                <svg width="100%" height="100%" viewBox={`0 0 ${svgSize} ${svgSize}`}>
                                    <foreignObject x={center - innerRadius} y={center - 40} width={innerRadius * 2} height="80">
                                        <div className="donut-center-text">
                                            {displayIndustry.title}
                                        </div>
                                    </foreignObject>

                                    {INDUSTRIES_DATA.map((industry, index) => {
                                        const startAngle = index * sliceAngle;
                                        const endAngle = startAngle + sliceAngle;
                                        const isActive = index === activeIndex;
                                        
                                        const midAngle = startAngle + (sliceAngle / 2);
                                        const midRadius = innerRadius + (outerRadius - innerRadius) / 2;
                                        const iconPos = polarToCartesian(center, center, midRadius, midAngle);

                                        const pathData = describeArc(center, center, innerRadius, outerRadius, startAngle, endAngle);
                                        const clipId = `clip-${industry.id}`;
                                        const imgSize = 210; 

                                        return (
                                            <g 
                                                key={industry.id} 
                                                onClick={() => setActiveIndex(index)}
                                                onMouseEnter={() => setHoveredIndex(index)}
                                                onMouseLeave={() => setHoveredIndex(null)}
                                                className={`donut-slice-group ${isActive ? 'active' : ''}`}
                                            >
                                                <defs>
                                                    <clipPath id={clipId}>
                                                        <path d={pathData} />
                                                    </clipPath>
                                                </defs>

                                                {getIndustryImage(industry.id) ? (
                                                    <image
                                                        href={getIndustryImage(industry.id)}
                                                        x={iconPos.x - imgSize / 2}
                                                        y={iconPos.y - imgSize / 2}
                                                        width={imgSize}
                                                        height={imgSize}
                                                        preserveAspectRatio="xMidYMid slice"
                                                        clipPath={`url(#${clipId})`}
                                                        className="donut-slice-image"
                                                    />
                                                ) : (
                                                    <path d={pathData} fill="#1a2533" />
                                                )}

                                                <path d={pathData} className="donut-slice-overlay" />
                                            </g>
                                        );
                                    })}
                                </svg>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={7} lg={8}>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeIndustry.id}
                                    initial={{ opacity: 0, x: 30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -30 }}
                                    transition={{ duration: 0.3, ease: "easeInOut" }}
                                >
                                    <Card className="industry-content-card">
                                        <Box className="industry-card-header">
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                                {/* Left Side: Title and Subtitle */}
                                                <Box>
                                                    <Typography component="h2" variant="h4" className="industry-card-title">
                                                        {activeIndustry.title}
                                                    </Typography>
                                                    <Typography component="p" variant="subtitle1" className="industry-card-subtitle">
                                                        {activeIndustry.subtitle}
                                                    </Typography>
                                                </Box>

                                                {/* Right Side: Amazon Logo */}
                                                {activeIndustry.logo && (
                                                    <Box sx={{ ml: 2, flexShrink: 0 }}>
                                                        <img 
                                                            src={activeIndustry.logo} 
                                                            alt={`${activeIndustry.title} logo`} 
                                                            style={{ 
                                                                height: '60px', /* Increased size */
                                                                width: 'auto',
                                                                maxWidth: '200px',
                                                                objectFit: 'contain', 
                                                                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' 
                                                            }} 
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                            
                                            {/* Problem Statement Below */}
                                            <Typography variant="body1" className="problem-statement" sx={{ mt: 1 }}>
                                                <strong>{t('industryApplicationsAmazon.page.theChallenge')}</strong> {activeIndustry.problem}
                                            </Typography>
                                        </Box>

                                        <Grid container spacing={4} sx={{ p: { xs: 2, md: 4 }, pt: 0 }}>
                                            <Grid item xs={12} md={6}>
                                                <Typography component="h3" variant="h6" sx={{ mb: 2, fontWeight: 700, color: 'var(--secondary-color)' }}>
                                                    {t('industryApplicationsAmazon.page.theSolution')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {activeIndustry.solutionPoints.map((point, idx) => (
                                                        <Box 
                                                            key={idx} 
                                                            className={`solution-point ${activeSolutionIndex === idx ? 'active' : ''}`}
                                                            onClick={() => setActiveSolutionIndex(idx)}
                                                        >
                                                            <Typography component="h4" variant="subtitle2" className="solution-title">
                                                                {point.title}
                                                            </Typography>
                                                            <Typography variant="body2" className="solution-text">
                                                                {point.text}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Grid>
                                            
                                            <Grid item xs={12} md={6}>
                                                <Box className="application-image-container">
                                                    <AnimatePresence mode="wait">
                                                        <motion.div
                                                            key={`${activeIndustry.id}-${activeSolutionIndex}`}
                                                            initial={{ opacity: 0 }}
                                                            animate={{ opacity: 1 }}
                                                            exit={{ opacity: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                            style={{ width: '100%', height: '100%', display: 'flex', position: 'relative' }}
                                                        >
                                                            {renderInteractiveMedia()}
                                                        </motion.div>
                                                    </AnimatePresence>
                                                </Box>
                                            </Grid>
                                        </Grid>

                                        {activeIndustry.impact && (
                                            <Box className="impact-statement">
                                                <Typography variant="body2">
                                                    <strong>{t('industryApplicationsAmazon.page.impact')}</strong> {activeIndustry.impact}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Card>
                                </motion.div>
                            </AnimatePresence>
                        </Grid>
                    </Grid>
                </Container>
            </main>
        </div>
    );
};

export default IndustryApplications;